import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { ProfilePhoto } from "@/components/AuthImage";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PaperPlaneTilt, ChatCircle } from "@phosphor-icons/react";
import { Monogram } from "@/components/AuthImage";

export default function Messages() {
  const { partnerId } = useParams();
  const [threads, setThreads] = useState([]);
  const [conv, setConv] = useState({ messages: [], partner: null });
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);
  const endRef = useRef();

  const loadThreads = async () => {
    const { data } = await api.get("/messages/threads");
    setThreads(data.threads || []);
    setLoading(false);
  };

  const loadConv = async (pid) => {
    if (!pid) { setConv({ messages: [], partner: null }); return; }
    const { data } = await api.get(`/messages/${pid}`);
    setConv(data);
    setTimeout(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
  };

  useEffect(() => { loadThreads(); }, []);
  useEffect(() => { loadConv(partnerId); }, [partnerId]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() || !partnerId) return;
    try {
      await api.post("/messages/send", { to_user_id: partnerId, body: text.trim() });
      setText("");
      await loadConv(partnerId);
      await loadThreads();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Message not sent");
    }
  };

  return (
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-10" data-testid="messages-page">
      <div className="label-eyebrow text-brand-terracotta">Inbox</div>
      <h1 className="font-serif text-5xl md:text-6xl tracking-tighter mt-2 text-brand-ink">Messages</h1>

      <div className="mt-8 grid grid-cols-12 gap-0 border border-brand-line bg-white min-h-[540px]">
        {/* threads */}
        <aside className="col-span-12 md:col-span-4 border-r border-brand-line max-h-[70vh] overflow-y-auto">
          {loading ? (
            <div className="p-8 text-brand-muted">Loading…</div>
          ) : threads.length === 0 ? (
            <div className="p-8 text-brand-muted text-sm" data-testid="threads-empty">
              No conversations yet. Accept an interest to begin chatting.
            </div>
          ) : threads.map((t) => (
            <Link
              key={t.partner.id}
              to={`/messages/${t.partner.id}`}
              className={`flex items-center gap-3 p-4 border-b border-brand-line hover:bg-brand-bone transition-colors ${partnerId === t.partner.id ? "bg-brand-bone" : ""}`}
              data-testid={`thread-${t.partner.id}`}
            >
              <div className="w-12 h-12 overflow-hidden rounded-full flex-shrink-0">
                <ProfilePhoto profile={t.partner} className="w-full h-full object-cover" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-brand-ink truncate">{t.partner.name}</div>
                <div className="text-xs text-brand-muted truncate">
                  {t.last_message.from_me ? "You: " : ""}{t.last_message.body}
                </div>
              </div>
            </Link>
          ))}
        </aside>

        {/* conversation */}
        <section className="col-span-12 md:col-span-8 flex flex-col max-h-[70vh]">
          {!partnerId ? (
            <div className="flex-1 flex flex-col items-center justify-center text-brand-muted">
              <ChatCircle size={44} weight="duotone" className="text-brand-terracotta" />
              <p className="mt-3 font-serif text-2xl text-brand-ink">Select a conversation</p>
              <p className="text-sm">Or find someone new to talk with in Browse.</p>
            </div>
          ) : (
            <>
              <header className="p-4 border-b border-brand-line flex items-center gap-3">
                <Link to={`/profile/${conv.partner?.id}`} className="w-10 h-10 rounded-full overflow-hidden">
                  <ProfilePhoto profile={conv.partner} className="w-full h-full object-cover" />
                </Link>
                <div>
                  <div className="font-medium text-brand-ink">{conv.partner?.name}</div>
                  <div className="text-xs text-brand-muted">{[conv.partner?.city, conv.partner?.profession].filter(Boolean).join(" · ")}</div>
                </div>
              </header>
              <div className="flex-1 overflow-y-auto p-6 space-y-3 bg-brand-bone" data-testid="conversation-body">
                {conv.messages.length === 0 && (
                  <div className="text-center text-sm text-brand-muted mt-16">Start the conversation with a warm hello.</div>
                )}
                {conv.messages.map((m) => (
                  <div key={m.id} className={`flex ${m.from_me ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[75%] px-4 py-2.5 text-sm ${m.from_me ? "bg-brand-terracotta text-white" : "bg-white border border-brand-line text-brand-ink"}`}>
                      {m.body}
                    </div>
                  </div>
                ))}
                <div ref={endRef} />
              </div>
              <form onSubmit={send} className="p-4 border-t border-brand-line flex gap-2">
                <Input
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder="Write a message…"
                  data-testid="message-input"
                  className="h-12 rounded-full border-brand-line"
                />
                <Button type="submit" data-testid="message-send-btn" className="h-12 rounded-full bg-brand-terracotta hover:bg-brand-terracotta/90 text-white px-5">
                  <PaperPlaneTilt size={16} weight="fill" />
                </Button>
              </form>
            </>
          )}
        </section>
      </div>
    </div>
  );
}
